import React from 'react';
import { motion } from 'framer-motion';
import BrandLogo from './BrandLogo';
import QuizTypeSelector from './QuizTypeSelector';
import AnimatedText, { ColorChangingText } from './AnimatedText';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiUser } = FiIcons;

const Welcome = ({ onStart }) => {
  const [selectedTarget, setSelectedTarget] = React.useState(null);

  const handleTargetSelect = (target) => {
    setSelectedTarget(target);
  };

  const handleQuizSelect = (quizType, targetType) => {
    onStart(targetType, quizType);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex items-center justify-center p-6"
    >
      <div className="max-w-4xl text-center">
        {/* Logo and Brand */}
        <AnimatedText delay={0.2} className="mb-8">
          <div className="flex flex-col items-center justify-center mb-6">
            <BrandLogo className="w-32 h-32 mb-4" />
            <div>
              <h1 className="text-5xl font-bold text-brand-crimson font-luxe mb-2">
                We The Billions
              </h1>
              <p className="text-brand-coffee/80 text-lg">
                86 billion neurons. <ColorChangingText className="font-bold">1 unique YOU.</ColorChangingText>
              </p>
            </div>
          </div>
        </AnimatedText>

        {/* Title */}
        <AnimatedText delay={0.4}>
          <h2 className="text-4xl md:text-5xl font-bold text-brand-coffee mb-6 font-luxe">
            Discover Your <ColorChangingText>Cognitive Profile</ColorChangingText>
          </h2>
        </AnimatedText>

        {/* Educational Content */}
        <AnimatedText delay={0.6}>
          <div className="bg-brand-floral/90 backdrop-blur-sm rounded-3xl p-8 mb-8 shadow-lg border-2 border-brand-dust/50">
            <h3 className="text-2xl font-bold text-brand-coffee mb-4 font-luxe">
              What Is A Brain Map?
            </h3>
            <p className="text-lg text-brand-coffee/90 mb-6 leading-relaxed">
              Your brain is a complex ecosystem. It's what helps you plan, focus, remember, regulate emotions, follow through, and bounce back. In short, it's how you <em>experience the world</em>.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              <motion.div whileHover={{ scale: 1.05 }} className="p-4 bg-brand-dust/20 rounded-xl border-l-4 border-brand-golden">
                <h4 className="font-bold text-brand-coffee mb-2">🧠 Basic Functions</h4>
                <p className="text-sm text-brand-coffee/80">
                  Keeps you alive—breathing, heart beating, basic survival. Some days, life feels like you're stuck here.
                </p>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} className="p-4 bg-brand-rose/10 rounded-xl border-l-4 border-brand-rose">
                <h4 className="font-bold text-brand-coffee mb-2">💗 Emotional Centers</h4>
                <p className="text-sm text-brand-coffee/80">
                  Where you feel—your emotions, stress, and safety responses. At times, we live here—overwhelmed or reactive.
                </p>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} className="p-4 bg-brand-crimson/10 rounded-xl border-l-4 border-brand-crimson">
                <h4 className="font-bold text-brand-crimson mb-2">🌟 Higher Thinking</h4>
                <p className="text-sm text-brand-coffee/80">
                  This is <strong>YOU</strong>. Your thoughts, dreams, and decisions. We help you live here—fully and intentionally.
                </p>
              </motion.div>
            </div>
            
            <motion.p 
              className="text-lg font-semibold text-brand-crimson mt-6"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              We help you move beyond survival and emotional overwhelm, guiding you to a place where you can fully access your best self.
            </motion.p>
          </div>
        </AnimatedText>

        {!selectedTarget ? (
          <AnimatedText delay={0.8}>
            <h3 className="text-2xl font-semibold text-brand-coffee mb-6 font-luxe">
              Who are you completing this assessment for?
            </h3>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.button
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleTargetSelect('self')}
                className="flex items-center justify-center px-8 py-4 bg-brand-crimson text-brand-floral rounded-2xl font-semibold shadow-lg hover:bg-brand-midnight transition-colors duration-300"
              >
                <SafeIcon icon={FiUser} className="mr-3 text-xl" />
                Myself
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleTargetSelect('other')}
                className="flex items-center justify-center px-8 py-4 bg-brand-golden text-brand-floral rounded-2xl font-semibold shadow-lg hover:bg-brand-coffee transition-colors duration-300"
              >
                <SafeIcon icon={FiUsers} className="mr-3 text-xl" />
                Someone Else
              </motion.button>
            </div>
          </AnimatedText>
        ) : (
          <QuizTypeSelector onSelect={handleQuizSelect} targetType={selectedTarget} />
        )}

        {/* Features */}
        <AnimatedText delay={1}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-brand-coffee/80 mt-8">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-brand-midnight rounded-full flex items-center justify-center mb-2">
                <span className="text-brand-floral font-bold">5</span>
              </div>
              <p>Brain regions</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-brand-crimson rounded-full flex items-center justify-center mb-2">
                <span className="text-brand-floral font-bold">45</span>
              </div>
              <p>Skills assessed</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-brand-golden rounded-full flex items-center justify-center mb-2">
                <span className="text-brand-floral font-bold">PDF</span>
              </div>
              <p>Printable report</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-brand-rose rounded-full flex items-center justify-center mb-2">
                <span className="text-brand-floral font-bold">⚡</span>
              </div>
              <p>Instant results</p>
            </div>
          </div>
        </AnimatedText>
      </div>
    </motion.div>
  );
};

export default Welcome;